import { differenceInCalendarDays, format, isValid, startOfDay } from 'date-fns';
import { formatMonthDateYear, getUTCDate } from '@dataGrid/gridUtilities';
import appConstants from '@appConstants';
import { checkIfDateIsWithinDateRange } from '../../../../../common/utils/dateTimeUtils';
import { driverConfirmationConstants } from './constants';

const HOLIDAY_DATE_SAVE_FORMAT = appConstants.DATETYPES.MONTHDATEYEAR.updateFormat;

export type ConfirmationDateRange = {
  startDate: Date | null;
  endDate: Date | null;
};

export type HolidayConfirmationDetails = {
  isCompanyHoliday?: boolean;
  isDriverConfirmationRequired?: boolean;
  holidayDate?: string | null;
  promptConfirmationDays?: number | null;
  confirmationDateRange?: ConfirmationDateRange | null;
};

export type HolidayConfirmationSavePayload = {
  isCompanyHoliday: boolean;
  isDriverConfirmationRequired: boolean;
  promptConfirmationDays: number | null;
  confirmationStartDate: string | null;
  confirmationEndDate: string | null;
};

const toCalendarDateFromApi = (dateString?: string | null): Date | null => {
  if (!dateString) {
    return null;
  }

  const parsedDate = getUTCDate(dateString);
  return isValid(parsedDate) ? startOfDay(parsedDate) : null;
};

const toCalendarDateFromPicker = (date?: Date | null): Date | null => {
  if (!date) {
    return null;
  }

  return isValid(date) ? startOfDay(date) : null;
};

const formatHolidayDateForSave = (date: Date): string =>
  format(date, HOLIDAY_DATE_SAVE_FORMAT);

export const getConfirmationDisplayRangeDayCount = (
  confirmationDateRange?: ConfirmationDateRange | null
): number | null => {
  const { startDate, endDate } = confirmationDateRange || {};

  if (!startDate || !endDate || !isValid(startDate) || !isValid(endDate)) {
    return null;
  }

  const normalizedStartDate = startOfDay(startDate);
  const normalizedEndDate = startOfDay(endDate);

  if (normalizedStartDate > normalizedEndDate) {
    return null;
  }

  return differenceInCalendarDays(normalizedEndDate, normalizedStartDate) + 1;
};

export const isHolidayDateWithinConfirmationRange = (
  holidayDate?: string | null,
  confirmationDateRange?: ConfirmationDateRange | null
): boolean => {
  const parsedHolidayDate = toCalendarDateFromApi(holidayDate);
  const { startDate, endDate } = confirmationDateRange || {};
  const parsedStartDate = toCalendarDateFromPicker(startDate);
  const parsedEndDate = toCalendarDateFromPicker(endDate);

  if (!parsedHolidayDate || !parsedStartDate || !parsedEndDate) {
    return false;
  }

  return checkIfDateIsWithinDateRange(
    parsedHolidayDate,
    parsedStartDate,
    parsedEndDate
  );
};

export const getConfirmationDisplayRangeError = (
  holidayDate?: string | null,
  confirmationDateRange?: ConfirmationDateRange | null
): string | null => {
  const { startDate, endDate } = confirmationDateRange || {};

  if (!startDate || !endDate) {
    return driverConfirmationConstants.MESSAGE.CONFIRMATION_DISPLAY_RANGE_REQUIRED;
  }

  const rangeDayCount = getConfirmationDisplayRangeDayCount(confirmationDateRange);

  if (
    rangeDayCount &&
    rangeDayCount > driverConfirmationConstants.MAX_CONFIRMATION_DISPLAY_RANGE_DAYS
  ) {
    return driverConfirmationConstants.MESSAGE.CONFIRMATION_DISPLAY_RANGE_MAX_DAYS;
  }

  if (!isHolidayDateWithinConfirmationRange(holidayDate, confirmationDateRange)) {
    return driverConfirmationConstants.MESSAGE.HOLIDAY_DATE_OUTSIDE_CONFIRMATION_RANGE;
  }

  return null;
};

export const getHolidayConfirmationErrors = (
  holidayDetails: HolidayConfirmationDetails
): {
  promptConfirmationDays: string | null;
  confirmationDateRange: string | null;
} => {
  if (
    holidayDetails.isCompanyHoliday ||
    !holidayDetails.isDriverConfirmationRequired
  ) {
    return {
      promptConfirmationDays: null,
      confirmationDateRange: null
    };
  }

  const { promptConfirmationDays } = holidayDetails;
  const isPromptDaysValid =
    promptConfirmationDays != null &&
    promptConfirmationDays >= driverConfirmationConstants.MIN_PROMPT_CONFIRMATION_DAYS &&
    promptConfirmationDays <= driverConfirmationConstants.MAX_PROMPT_CONFIRMATION_DAYS;

  return {
    promptConfirmationDays: isPromptDaysValid ? null : '* Required',
    confirmationDateRange: getConfirmationDisplayRangeError(
      holidayDetails.holidayDate,
      holidayDetails.confirmationDateRange
    )
  };
};

export const formatConfirmationDatesForSave = (
  confirmationDateRange?: ConfirmationDateRange | null
): { confirmationStartDate: string; confirmationEndDate: string } | null => {
  const { startDate, endDate } = confirmationDateRange || {};

  if (!startDate || !endDate) {
    return null;
  }

  return {
    confirmationStartDate: formatHolidayDateForSave(startDate),
    confirmationEndDate: formatHolidayDateForSave(endDate)
  };
};

/** Builds confirmation fields for create and update saveHoliday payloads. */
export const getConfirmationSavePayload = (
  holidayDetails: HolidayConfirmationDetails
): HolidayConfirmationSavePayload => {
  const isCompanyHoliday = holidayDetails.isCompanyHoliday;
  const sharedPayload = {
    isCompanyHoliday
  };

  // Company holidays never send driver confirmation fields.
  if (isCompanyHoliday || !holidayDetails.isDriverConfirmationRequired) {
    return {
      ...sharedPayload,
      isDriverConfirmationRequired: false,
      promptConfirmationDays: null,
      confirmationStartDate: null,
      confirmationEndDate: null
    };
  }

  const confirmationDates = formatConfirmationDatesForSave(
    holidayDetails.confirmationDateRange
  );

  return {
    ...sharedPayload,
    isDriverConfirmationRequired: true,
    promptConfirmationDays: holidayDetails.promptConfirmationDays ?? null,
    confirmationStartDate: confirmationDates?.confirmationStartDate ?? null,
    confirmationEndDate: confirmationDates?.confirmationEndDate ?? null
  };
};

export const parseConfirmationDateRangeFromApi = (
  holidayDetails?: {
    confirmationStartDate?: string | null;
    confirmationEndDate?: string | null;
  } | null
): ConfirmationDateRange => {
  if (!holidayDetails?.confirmationStartDate || !holidayDetails?.confirmationEndDate) {
    return { startDate: null, endDate: null };
  }

  return {
    startDate: toCalendarDateFromApi(holidayDetails.confirmationStartDate),
    endDate: toCalendarDateFromApi(holidayDetails.confirmationEndDate)
  };
};

export const formatConfirmationDateRangeDisplay = (
  holidayDetails?: {
    confirmationStartDate?: string | null;
    confirmationEndDate?: string | null;
  } | null
): string => {
  if (!holidayDetails?.confirmationStartDate || !holidayDetails?.confirmationEndDate) {
    return '';
  }

  return `${formatMonthDateYear({ value: holidayDetails.confirmationStartDate })} - ${formatMonthDateYear({
    value: holidayDetails.confirmationEndDate
  })}`;
};

export const confirmationDateRangeValueGetter = (params: {
  data?: {
    isDriverConfirmationRequired?: boolean;
    confirmationStartDate?: string | null;
    confirmationEndDate?: string | null;
  };
}): string[] => {
  if (!params.data?.isDriverConfirmationRequired) {
    return [];
  }

  const { confirmationStartDate, confirmationEndDate } = params.data;

  if (!confirmationStartDate || !confirmationEndDate) {
    return [];
  }

  return [confirmationStartDate, confirmationEndDate].map((date) =>
    formatMonthDateYear({ value: date })
  );
};
